<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
// Zimobi: the cards ("chamados") tab shares the contact sidebar.
import ConversationCardsButton from 'custom/dashboard/components-next/Conversation/ConversationCardsButton.vue';
import { useConversationSidebarTab } from 'custom/dashboard/composables/zimobi/useConversationSidebarTab';

const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

// Zimobi: opening the contact panel also switches the sidebar away from the cards tab.
const { isCardsTabOpen, openTab } = useConversationSidebarTab();
const handleConversationSidebarToggle = () => openTab('contact');

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <!--
    Zimobi: on mobile (< md) the contact panel is toggled by the legacy
    "Detalhes" tab docked to the right edge. On desktop (>= md) we keep the
    upstream floating pill (contact + copilot).
  -->
  <Button
    solid
    xs
    slate
    class="md:hidden !fixed z-10 top-32 ltr:right-0 rtl:left-0 rtl:rotate-180 w-6 !h-[5.5rem] !rounded-xl !rounded-r-none flex flex-col justify-between !bg-n-solid-3 hover:enabled:!bg-n-solid-2 border border-n-weak shadow-sm"
    :icon="
      isContactSidebarOpen ? 'i-ph-caret-right-fill' : 'i-ph-caret-left-fill'
    "
    @click="toggleConversationSidebarToggle"
  >
    <span class="flex transform -rotate-90 mb-6 capitalize">
      {{ $t('CONVERSATION.HEADER.DETAILS') }}
    </span>
  </Button>
  <ButtonGroup
    class="hidden md:flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        'bg-n-alpha-2 active:shadow-sm':
          isContactSidebarOpen && !isCardsTabOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <!-- Zimobi: opens the conversation's cards in the sidebar, with the open count as a badge. -->
    <ConversationCardsButton />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
  </ButtonGroup>
</template>
